/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  BarChart,
  CandlestickChart,
  CustomChart,
  LineChart,
} from "echarts/charts";
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  LegendScrollComponent,
  TitleComponent,
  TooltipComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import moment from "moment-timezone";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSocket } from "src/components/SocketContext";
import { DataPoint } from "src/helpers";
import {
  formatNumber,
  formatNumberWithSuffix,
  getLastTwelveMonthIndices,
  getMonthName,
} from "src/helpers/api/sharedFunction";
import {
  computeRollingAverageVolumes,
  computeVolumeWidthRatios,
  createVolumeBarRenderItem,
  createVolumeCandleRenderItem,
  isVolumeAvgPeriodOption,
  mergeVolumeWidthConfig,
  normalizeVolumeAvgPeriodDays,
  VOL_BASED_CANDLE_LEGEND_NAME,
  VOLUME_AVG_PERIOD_OPTIONS,
  type VolumeAvgPeriodOption,
  type VolumeWidthConfig,
} from "src/common/charts/volumeWidthUtils";
import { isOhlcvHeaderHidden } from "src/helpers/chart-helpers";

const STANDARD_CANDLESTICK_ITEM_STYLE = {
  color: "#089981",
  color0: "#FD1050",
  borderColor: "#089981",
  borderColor0: "#FD1050",
};

const TRANSPARENT_CANDLESTICK_ITEM_STYLE = {
  color: "transparent",
  color0: "transparent",
  borderColor: "transparent",
  borderColor0: "transparent",
};

const VOL_PERIOD_TRIGGER_WIDTH_PX = 46;

type ZrLegendItem = {
  __legendDataIndex?: number;
  getBoundingRect(): { x: number; y: number; width: number; height: number };
  transformCoordToGlobal(x: number, y: number): [number, number];
};

function getVolLegendDataIndex(
  legendData: Array<string | { name: string }>,
): number {
  return legendData.findIndex((item) => {
    const name = typeof item === "string" ? item : item.name;
    return name === VOL_BASED_CANDLE_LEGEND_NAME;
  });
}

/** Read the rendered "Vol. width" legend item position (works with scroll legend). */
function measureVolPeriodTriggerLeft(
  chart: echarts.EChartsType,
): number | null {
  // ECharts exposes legend layout via internal view APIs (not on public types).
  const chartInternals = chart as any;

  const model = chartInternals.getModel();
  if (!model) return null;
  const legendModel = model.getComponent("legend", 0);
  if (!legendModel) return null;

  const legendData = legendModel.get("data") as Array<
    string | { name: string }
  >;
  const volLegendIndex = getVolLegendDataIndex(legendData);
  if (volLegendIndex < 0) return null;

  const legendView = chartInternals.getViewOfComponentModel(legendModel);
  const contentGroup = legendView?.getContentGroup();
  if (!contentGroup) return null;

  const legendItems: ZrLegendItem[] = [];
  contentGroup.eachChild((child: ZrLegendItem) => {
    legendItems.push(child);
  });
  const targetItem = legendItems.find(
    (child) => child.__legendDataIndex === volLegendIndex,
  );
  if (!targetItem) return null;

  const rect = targetItem.getBoundingRect();
  const [rightX] = targetItem.transformCoordToGlobal(
    rect.x + rect.width,
    rect.y + rect.height / 2,
  );

  const chartWidth = chart.getWidth();
  if (rightX < 8 || rightX > chartWidth - 8) return null;

  return Math.max(
    4,
    Math.min(rightX + 4, chartWidth - VOL_PERIOD_TRIGGER_WIDTH_PX - 4),
  );
}

function scrollLegendToVolWidth(chart: echarts.EChartsType) {
  const legendModel = (chart as any).getModel().getComponent("legend", 0);
  if (!legendModel || legendModel.get("type") !== "scroll") return;

  const legendData = legendModel.get("data") as Array<
    string | { name: string }
  >;
  const volLegendIndex = getVolLegendDataIndex(legendData);
  if (volLegendIndex < 0) return;

  // Don't scroll if the item is already visible in the viewport
  if (measureVolPeriodTriggerLeft(chart) !== null) return;

  chart.dispatchAction({
    type: "legendScroll",
    // Scroll to the start of the page containing the item to prevent breaking pagination
    scrollDataIndex: Math.max(0, volLegendIndex - 1),
  });
}

type OhlcTuple = [number, number, number, number];

function parseOhlcValue(item: unknown): OhlcTuple | null {
  if (item === "-" || item == null) return null;
  const raw = Array.isArray(item)
    ? item
    : typeof item === "object" && "value" in (item as object)
      ? (item as { value: unknown }).value
      : item;
  if (!Array.isArray(raw) || raw.length < 4) return null;
  const open = Number(raw[0]);
  const close = Number(raw[1]);
  const low = Number(raw[2]);
  const high = Number(raw[3]);
  if ([open, close, low, high].some((v) => Number.isNaN(v))) return null;
  return [open, close, low, high];
}

function parseLineValue(item: unknown): number | null {
  if (item === "-" || item == null) return null;
  const val =
    typeof item === "object" && item !== null && "value" in item
      ? Number((item as { value: unknown }).value)
      : Number(item);
  return Number.isFinite(val) ? val : null;
}

/** Index after the last real candle (excludes trailing BUFFER_DAYS placeholders). */
function getRealDataLength(data: unknown[]): number {
  for (let i = data.length - 1; i >= 0; i--) {
    if (parseOhlcValue(data[i])) return i + 1;
    const vol = parseVolumeValue(data[i]);
    if (vol != null) return i + 1;
  }
  return data.length;
}

function parseVolumeValue(item: unknown): number | null {
  if (item === "-" || item == null) return null;
  if (Array.isArray(item)) return parseVolumeValue(item[0]);
  if (typeof item === "object" && "value" in (item as object)) {
    const val = (item as { value: unknown }).value;
    return Array.isArray(val)
      ? parseVolumeValue(val[0])
      : parseVolumeValue(val);
  }
  const vol = Number(item);
  return Number.isFinite(vol) ? vol : null;
}

/**
 * The market feed can report open=0 for the current (today's) candle before the
 * opening print arrives. Drawing the body from 0 makes the candle stretch across
 * the whole grid and cross the x-axis, so we fall back to close (LTP) for the
 * RENDERED candle only. The raw OHLC data used by the header/tooltip is untouched.
 */
function getCandleRenderOpen(open: number, close: number): number {
  return open > 0 ? open : close;
}

/** Price-axis range for the current dataZoom window (matches default candlestick scaling). */
function computeVisiblePriceYRange(
  ohlcData: unknown[],
  startPercent: number,
  endPercent: number,
  extraLineSeries?: { data: unknown[]; include: boolean }[],
  extraLevels?: number[],
  realDataLength?: number,
): { min: number; max: number } | null {
  const len = ohlcData.length;
  if (len === 0) return null;

  const dataEnd = realDataLength ?? getRealDataLength(ohlcData);
  const startIdx = Math.max(0, Math.floor((startPercent / 100) * len));
  const endIdx = Math.min(len, Math.ceil((endPercent / 100) * len), dataEnd);
  if (startIdx >= endIdx) return null;

  let min = Infinity;
  let max = -Infinity;

  for (let i = startIdx; i < endIdx; i++) {
    const ohlc = parseOhlcValue(ohlcData[i]);
    if (ohlc) {
      min = Math.min(min, ohlc[2]);
      max = Math.max(max, ohlc[3]);
    }
  }

  extraLineSeries?.forEach((series) => {
    if (!series.include) return;
    for (let i = startIdx; i < endIdx; i++) {
      const v = parseLineValue(series.data[i]);
      if (v != null) {
        min = Math.min(min, v);
        max = Math.max(max, v);
      }
    }
  });

  extraLevels?.forEach((level) => {
    if (Number.isFinite(level)) {
      min = Math.min(min, level);
      max = Math.max(max, level);
    }
  });

  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;

  const span = max - min || Math.abs(max) * 0.01 || 1;
  const pad = span * 0.1;
  return { min: min - pad, max: max + pad };
}

function computeVisibleVolumeYMax(
  volumeData: unknown[],
  startPercent: number,
  endPercent: number,
  realDataLength?: number,
): number | null {
  const len = volumeData.length;
  if (len === 0) return null;

  const dataEnd = realDataLength ?? getRealDataLength(volumeData);
  if (dataEnd <= 0) return null;

  const startIdx = Math.max(0, Math.floor((startPercent / 100) * len));
  const endIdx = Math.min(Math.ceil((endPercent / 100) * len), dataEnd);
  if (startIdx >= endIdx) return null;

  const visibleVolumes: number[] = [];
  for (let i = startIdx; i < endIdx; i++) {
    const vol = parseVolumeValue(volumeData[i]);
    if (vol != null && vol > 0) visibleVolumes.push(vol);
  }
  if (visibleVolumes.length === 0) return null;

  visibleVolumes.sort((a, b) => a - b);
  const absoluteMax = visibleVolumes[visibleVolumes.length - 1];

  // Robust percentile ceiling: if the peak is an extreme single-day spike (> 2.5x the 95th percentile),
  // cap the Y-axis ceiling at 1.5x of the 95th percentile so regular daily volume patterns remain clearly visible.
  const p95Index = Math.floor(visibleVolumes.length * 0.95);
  const p95 = visibleVolumes[p95Index] || absoluteMax;

  if (absoluteMax > p95 * 2.5) {
    return p95 * 1.5;
  }

  return absoluteMax;
}

function getNiceMax(val: number): number {
  if (val <= 0) return val;
  const exp = Math.floor(Math.log10(val));
  const frac = val / Math.pow(10, exp);
  let niceFrac = 10;
  if (frac <= 1.2) niceFrac = 1.2;
  else if (frac <= 1.5) niceFrac = 1.5;
  else if (frac <= 2) niceFrac = 2;
  else if (frac <= 3) niceFrac = 3;
  else if (frac <= 4) niceFrac = 4;
  else if (frac <= 5) niceFrac = 5;
  else if (frac <= 6) niceFrac = 6;
  else if (frac <= 8) niceFrac = 8;
  return niceFrac * Math.pow(10, exp);
}

/** Keep price + volume y-axes aligned with the visible dataZoom window (excludes buffer days). */
function syncAxesToVisibleWindow(chart: echarts.EChartsType) {
  const current = chart.getOption() as {
    legend?: { selected?: Record<string, boolean> }[];
    dataZoom?: { start?: number; end?: number }[];
    yAxis?: { min?: number | null; max?: number | null; scale?: boolean }[];
    series?: {
      type?: string;
      name?: string;
      data?: unknown[];
    }[];
  };

  const legendSelected = current.legend?.[0]?.selected ?? {};
  const dataZoom = current.dataZoom?.[0];
  const startPct = dataZoom?.start ?? 0;
  const endPct = dataZoom?.end ?? 100;

  const candleSeries = current.series?.find((s) => s.type === "candlestick");
  if (!candleSeries?.data) return;

  const realDataLength = getRealDataLength(candleSeries.data);

  const extraLines =
    current.series
      ?.filter(
        (s) =>
          s.type === "line" &&
          s.name &&
          s.name !== "Average" &&
          s.name !== "Avg Vol",
      )
      .map((s) => ({
        data: s.data ?? [],
        include: legendSelected[s.name!] !== false,
      })) ?? [];

  const extraLevels: number[] = [];
  if (legendSelected.Average !== false) {
    const avgSeries = current.series?.find((s) => s.name === "Average");
    const avgLine = (
      avgSeries as { markLine?: { data?: { yAxis?: number }[] } }
    )?.markLine?.data?.[0]?.yAxis;
    if (typeof avgLine === "number") extraLevels.push(avgLine);
  }

  const priceRange = computeVisiblePriceYRange(
    candleSeries.data,
    startPct,
    endPct,
    extraLines,
    extraLevels,
    realDataLength,
  );

  const volumeSeries = current.series?.find(
    (s) => s.name?.toLowerCase() === "volume",
  );
  const volumeMax = volumeSeries?.data
    ? computeVisibleVolumeYMax(
        volumeSeries.data,
        startPct,
        endPct,
        realDataLength,
      )
    : null;

  const nextPriceAxis = priceRange
    ? { min: priceRange.min, max: priceRange.max, scale: false as const }
    : { min: null, max: null, scale: true as const };
  const currentYAxis = current.yAxis;
  const currentPriceY = currentYAxis?.[0];
  const currentVolumeY = currentYAxis?.[1];

  const nextVolumeMax = volumeMax ? getNiceMax(volumeMax) : null;

  if (
    currentPriceY?.min === nextPriceAxis.min &&
    currentPriceY?.max === nextPriceAxis.max &&
    currentPriceY?.scale === nextPriceAxis.scale &&
    currentVolumeY?.min === 0 &&
    currentVolumeY?.max === nextVolumeMax
  ) {
    return;
  }

  chart.setOption({
    yAxis: [nextPriceAxis, { min: 0, max: nextVolumeMax }],
  });
}

const VolumePeriodMenu = ({
  open,
  anchor,
  value,
  onChange,
  onClose,
  darkMode,
  containerWidth,
}: {
  open: boolean;
  anchor: { x: number; y: number };
  value: VolumeAvgPeriodOption;
  onChange: (days: VolumeAvgPeriodOption) => void;
  onClose: () => void;
  darkMode: boolean;
  containerWidth: number;
}) => {
  const menuRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const menuWidth = 56;
  const clampedLeft =
    containerWidth > 0
      ? Math.max(0, Math.min(anchor.x, containerWidth - menuWidth))
      : anchor.x;

  return (
    <ul
      ref={menuRef}
      role="listbox"
      aria-label="Volume averaging period in days"
      className={`absolute z-20 min-w-[3.25rem] overflow-hidden rounded border py-0.5 shadow-md ${
        darkMode
          ? "border-gray-600/70 bg-[#252530] text-gray-200"
          : "border-gray-300/80 bg-white text-gray-700"
      }`}
      style={{ left: clampedLeft, top: anchor.y }}
    >
      {VOLUME_AVG_PERIOD_OPTIONS.map((days) => (
        <li key={days} role="option" aria-selected={days === value}>
          <button
            type="button"
            onClick={() => onChange(days)}
            className={`block w-full cursor-pointer px-2 py-0.5 text-left text-[9px] font-semibold leading-4 transition-colors ${
              days === value
                ? darkMode
                  ? "bg-cyan-900/40 text-cyan-200"
                  : "bg-teal-50 text-teal-800"
                : darkMode
                  ? "hover:bg-gray-700/80"
                  : "hover:bg-gray-100"
            }`}
          >
            {days}d avg
          </button>
        </li>
      ))}
    </ul>
  );
};

const VolumePeriodTrigger = ({
  value,
  darkMode,
  left,
  visible,
  onClick,
}: {
  value: VolumeAvgPeriodOption;
  darkMode: boolean;
  left: number;
  visible: boolean;
  onClick: (anchor: { x: number; y: number }) => void;
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);

  if (!visible) return null;

  return (
    <button
      ref={triggerRef}
      type="button"
      title="Volume averaging period — scales candle & volume bar width"
      aria-label="Change volume averaging period"
      onClick={() => {
        const el = triggerRef.current;
        if (!el) return;
        const parent = el.offsetParent as HTMLElement | null;
        const parentRect = parent?.getBoundingClientRect();
        const rect = el.getBoundingClientRect();
        onClick({
          x: rect.left - (parentRect?.left ?? 0),
          y: rect.bottom - (parentRect?.top ?? 0) + 2,
        });
      }}
      className={`absolute z-10 h-4 cursor-pointer rounded border px-1 text-[9px] font-semibold leading-4 shadow-sm transition-[border-color,box-shadow] focus:outline-none focus:ring-1 ${
        darkMode
          ? "border-gray-600/70 bg-[#252530]/90 text-gray-200 focus:ring-cyan-500/35 hover:border-gray-500"
          : "border-gray-300/80 bg-white/90 text-gray-700 focus:ring-teal-500/25 hover:border-gray-400"
      }`}
      style={{ left, top: 2 }}
    >
      {value}d ▾
    </button>
  );
};

echarts.use([
  CandlestickChart,
  CustomChart,
  LineChart,
  BarChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DataZoomComponent,
  LegendComponent,
  LegendScrollComponent,
  CanvasRenderer,
]);

type BarData = { value: number };
type CandlestickData = [number, number, number, number, number]; // [timestamp, open, close, low, high]
type LineData = number;

const Candle = ({
  candleData,
  darkMode,
  isExpand,
  isAnalyzeStock,
  obDate,
  retestDate,
  isOrderblock,
  fromDate,
  toDate,
  transactionType,
  symbolName,
  onHoverChange,
  /** Initial rolling volume window (10, 20, 50, 100, 200). Defaults to 20; user can change via Vol. width dropdown. */
  volumeAvgPeriodDays,
  volumeWidthConfig: volumeWidthConfigProp,
  historicalDates,
  onHistoricalPointerClick,
}: {
  candleData: DataPoint[];
  darkMode: boolean;
  isExpand?: boolean;
  isOrderblock?: boolean;
  isAnalyzeStock?: boolean;
  obDate?: string | undefined;
  fromDate?: string | undefined;
  retestDate?: string | undefined;
  toDate?: string | undefined;
  transactionType?: string;
  isOrderBlock?: boolean;
  symbolName?: string;
  onHoverChange?: (candle: null) => void;
  volumeAvgPeriodDays?: number;
  volumeWidthConfig?: Partial<VolumeWidthConfig>;
  historicalDates?: string[];
  onHistoricalPointerClick?: (date: string) => void;
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.EChartsType | null>(null);
  const lastSymbolRef = useRef<string | undefined>(symbolName);
  const { isConnected, socket } = useSocket();

  // Scope legend preference dynamically based on the current module's route
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const moduleName = pathname.split("/")[1] || "default";

  const LEGEND_STORAGE_KEY = `candle_legend_selected_${moduleName}`;
  const LEGEND_SYNC_EVENT = `candle-legend-changed-${moduleName}`;
  const VOL_PERIOD_STORAGE_KEY = `candle_volume_avg_period_${moduleName}`;
  const VOL_PERIOD_SYNC_EVENT = `candle-volume-period-changed-${moduleName}`;

  const readSavedVolumePeriod = (): VolumeAvgPeriodOption => {
    try {
      const saved = localStorage.getItem(VOL_PERIOD_STORAGE_KEY);
      if (saved != null) {
        const parsed = Number(saved);
        if (isVolumeAvgPeriodOption(parsed)) {
          return parsed;
        }
      }
    } catch {
      // ignore storage errors
    }
    return normalizeVolumeAvgPeriodDays(volumeAvgPeriodDays);
  };

  const [volumePeriodDays, setVolumePeriodDays] =
    useState<VolumeAvgPeriodOption>(readSavedVolumePeriod);
  const [volumePeriodMenu, setVolumePeriodMenu] = useState<{
    open: boolean;
    x: number;
    y: number;
  }>({ open: false, x: 0, y: 0 });
  const [volWidthLegendOn, setVolWidthLegendOn] = useState(false);
  const [volPeriodTriggerLeft, setVolPeriodTriggerLeft] = useState(0);
  const [volPeriodTriggerVisible, setVolPeriodTriggerVisible] = useState(false);
  const [chartContainerWidth, setChartContainerWidth] = useState(0);

  const closeVolumePeriodMenu = useCallback(
    () => setVolumePeriodMenu((prev) => ({ ...prev, open: false })),
    [],
  );

  const openVolumePeriodMenu = useCallback(
    (anchor: { x: number; y: number }) =>
      setVolumePeriodMenu({ open: true, x: anchor.x, y: anchor.y }),
    [],
  );

  const syncVolPeriodTriggerPosition = useCallback(() => {
    const chart = chartInstance.current;
    const container = chartContainerRef.current;
    if (container) {
      setChartContainerWidth(container.clientWidth);
    }
    if (!chart || !volWidthLegendOn) {
      setVolPeriodTriggerVisible(false);
      return;
    }
    const left = measureVolPeriodTriggerLeft(chart);
    if (left == null) {
      setVolPeriodTriggerVisible(false);
      return;
    }
    setVolPeriodTriggerLeft(left);
    setVolPeriodTriggerVisible(true);
  }, [volWidthLegendOn]);

  const syncVolPeriodTriggerPositionRef = useRef(syncVolPeriodTriggerPosition);
  useEffect(() => {
    syncVolPeriodTriggerPositionRef.current = syncVolPeriodTriggerPosition;
  }, [syncVolPeriodTriggerPosition]);

  const saveVolumePeriod = (days: VolumeAvgPeriodOption) => {
    try {
      localStorage.setItem(VOL_PERIOD_STORAGE_KEY, String(days));
      window.dispatchEvent(
        new CustomEvent(VOL_PERIOD_SYNC_EVENT, { detail: days }),
      );
    } catch {
      // ignore storage errors
    }
    setVolumePeriodDays(days);
  };

  const getSavedLegendSelected = (): Record<string, boolean> => {
    try {
      const saved = localStorage.getItem(LEGEND_STORAGE_KEY);
      const parsed: Record<string, boolean> = saved ? JSON.parse(saved) : {};

      return parsed;
    } catch {
      return {};
    }
  };

  const saveLegendSelected = (selected: Record<string, boolean>) => {
    try {
      localStorage.setItem(LEGEND_STORAGE_KEY, JSON.stringify(selected));
      // Notify the sibling chart instance (card ↔ expand modal)
      window.dispatchEvent(
        new CustomEvent(LEGEND_SYNC_EVENT, { detail: selected }),
      );
    } catch {
      // ignore storage errors
    }
  };

  // The chart instance is created once, so its long-lived event handlers read the
  // current props through refs instead of closing over the values of one render.
  const isExpandRef = useRef(isExpand);
  const onHoverChangeRef = useRef(onHoverChange);
  const onHistoricalPointerClickRef = useRef(onHistoricalPointerClick);
  const historicalDatesRef = useRef(historicalDates);
  const saveLegendSelectedRef = useRef(saveLegendSelected);
  useEffect(() => {
    isExpandRef.current = isExpand;
    onHoverChangeRef.current = onHoverChange;
    onHistoricalPointerClickRef.current = onHistoricalPointerClick;
    historicalDatesRef.current = historicalDates;
    saveLegendSelectedRef.current = saveLegendSelected;
  });

  const filteredCandleData = candleData;

  const volumeWidthConfig = useMemo(
    () =>
      mergeVolumeWidthConfig({
        ...volumeWidthConfigProp,
        avgPeriodDays: volumePeriodDays,
      }),
    [volumeWidthConfigProp, volumePeriodDays],
  );

  const rawVolumes = useMemo(
    () => filteredCandleData.map((item) => Number(item.volume) || 0),
    [filteredCandleData],
  );

  const rollingAvgVolumes = useMemo(
    () => computeRollingAverageVolumes(rawVolumes, volumePeriodDays),
    [rawVolumes, volumePeriodDays],
  );

  const volumeWidthRatios = useMemo(
    () => computeVolumeWidthRatios(rawVolumes, volumeWidthConfig),
    [rawVolumes, volumeWidthConfig],
  );

  const dates = filteredCandleData.map((item) => {
    return moment.utc(item?.date).tz("Asia/Kolkata").format("YYYY-MM-DD");
  });

  const normalizeDate = (dateStr?: string) => {
    if (!dateStr) return undefined;
    return moment.utc(dateStr).tz("Asia/Kolkata").format("YYYY-MM-DD");
  };

  const formattedOBDate = normalizeDate(obDate);
  const formattedRetestDate = normalizeDate(retestDate);

  const obIndex = formattedOBDate
    ? dates.findIndex((d) => d === formattedOBDate)
    : -1;

  const retestIndex = formattedRetestDate
    ? dates.findIndex((d) => d === formattedRetestDate)
    : -1;

  const todayStr = moment().tz("Asia/Kolkata").format("YYYY-MM-DD");
  const ohlc = filteredCandleData.map((item) => {
    const dStr = moment.utc(item?.date).tz("Asia/Kolkata").format("YYYY-MM-DD");
    const isToday = dStr === todayStr && isConnected;
    const renderOpen = getCandleRenderOpen(item.open, item.close);

    if (isToday) {
      const activeColor = item.close >= renderOpen ? "#089981" : "#F23645";

      return {
        value: [renderOpen, item.close, item.low, item.high],
        itemStyle: {
          color: activeColor,
          color0: activeColor,
          borderColor: activeColor,
          borderColor0: activeColor,
          borderWidth: 2,
          shadowBlur: 10, // The subtle glow
          shadowColor: activeColor,
        },
      };
    }
    return [renderOpen, item.close, item.low, item.high];
  });

  const lastCandle = filteredCandleData[filteredCandleData.length - 1];
  const lastClose = lastCandle ? lastCandle.close : 0;
  const isLastToday =
    lastCandle &&
    moment.utc(lastCandle.date).tz("Asia/Kolkata").format("YYYY-MM-DD") ===
      todayStr;
  const lastCandleIsBullish = lastCandle
    ? lastCandle.close >= lastCandle.open
    : true;
  const lastCandleColor = lastCandleIsBullish ? "#089981" : "#FD1050";

  const categoryData = filteredCandleData.map((item) => item.date); // x-axis dates

  const totalStart = new Date(categoryData[0]).getTime();
  const totalEnd = new Date(categoryData[categoryData.length - 1]).getTime();
  const totalDuration = totalEnd - totalStart;

  const twoMonths = 1000 * 60 * 60 * 24 * 30 * 2; // approx 2 months
  const minSpanPercentage = (twoMonths / totalDuration) * 100;

  const lastDate = new Date(dates[dates.length - 1]);
  const fiveMonthsAgo = new Date(lastDate);
  fiveMonthsAgo.setMonth(lastDate.getMonth() - 5);

  const getLabelText = (type: string | null | undefined) => {
    if (!type) return "";
    const lower = type.toLowerCase();

    if (lower.includes("sell") || lower.includes("sold")) {
      return "Sold here";
    }
    if (lower.includes("buy") || lower.includes("purchase")) {
      return "Bought here";
    }

    return type;
  };

  let startIndex = dates.findIndex((d) => new Date(d) >= fiveMonthsAgo);
  if (startIndex === -1) startIndex = 0;

  const displaySymbolName = symbolName || "Candle";

  const orderBlockMarkArea: any[] = [];

  if (obIndex !== -1 && ohlc[obIndex]) {
    const obCandle = ohlc[obIndex];
    const obCandleValue = Array.isArray(obCandle)
      ? obCandle
      : (obCandle as any).value;
    const obLow = obCandleValue[2];
    const obHigh = obCandleValue[3];
    const obEndIndex =
      retestIndex !== -1 && retestIndex > obIndex
        ? retestIndex
        : dates.length - 1;
    orderBlockMarkArea.push([
      {
        name: "Order Block Zone",
        itemStyle: {
          color: darkMode
            ? "rgba(0, 180, 255, 0.3)"
            : "rgba(50, 150, 255, 0.2)",
        },
        label: { show: false },
        xAxis: dates[obIndex],
        yAxis: obLow,
      },
      {
        xAxis: dates[obEndIndex],
        yAxis: obHigh,
      },
    ]);
    orderBlockMarkArea.push([
      {
        name: "OB Created",
        itemStyle: {
          color: "transparent",
          borderColor: darkMode ? "#00B4FF" : "#0966C8",
          borderWidth: 1,
        },
        label: { show: false },
        xAxis: dates[obIndex],
        yAxis: obLow,
      },
      {
        xAxis: dates[obIndex],
        yAxis: obHigh,
      },
    ]);
  }

  if (retestIndex !== -1 && ohlc[retestIndex]) {
    const retestCandle = ohlc[retestIndex];
    const retestCandleValue = Array.isArray(retestCandle)
      ? retestCandle
      : (retestCandle as any).value;
    const retestLow = retestCandleValue[2];
    const retestHigh = retestCandleValue[3];
    orderBlockMarkArea.push([
      {
        name: "Retest Touch",
        itemStyle: {
          color: "transparent",
          borderColor: darkMode ? "#FFA500" : "#FF8C00",
          borderWidth: 1,
        },
        label: { show: false },
        xAxis: dates[retestIndex],
        yAxis: retestLow,
      },
      {
        xAxis: dates[retestIndex],
        yAxis: retestHigh,
      },
    ]);
  }

  const normalizedFrom = normalizeDate(fromDate);
  const normalizedTo = normalizeDate(toDate);
  const candlestickMarkPoints: any[] = [];
  const historicalMarkPoints: any[] = [];

  if (normalizedFrom) {
    const fromIndex = dates.findIndex((d) => d === normalizedFrom);
    const toIndex = normalizedTo
      ? dates.findIndex((d) => d === normalizedTo)
      : -1;
    if (fromIndex !== -1) {
      const fromCandleValue = Array.isArray(ohlc[fromIndex])
        ? ohlc[fromIndex]
        : (ohlc[fromIndex] as any).value;
      candlestickMarkPoints.push({
        name: transactionType || "Start",
        coord: [dates[fromIndex], fromCandleValue[3]],
        symbol: "pin",
        symbolSize: 16,
        itemStyle: {
          color: darkMode ? "#8f8d7bff" : "#ada997ff",
          shadowBlur: 10,
          shadowColor: darkMode ? "#fff" : "#333",
        },
        label: {
          show: true,
          position: "top",
          formatter: getLabelText(transactionType),
          color: "#fff",
          backgroundColor: darkMode ? "rgba(255,255,255,0.2)" : "#000",
          borderRadius: 4,
          padding: [4, 6],
          fontSize: 12,
        },
      });

      if (toIndex !== -1 && toIndex !== fromIndex) {
        const toCandleValue = Array.isArray(ohlc[toIndex])
          ? ohlc[toIndex]
          : (ohlc[toIndex] as any).value;
        candlestickMarkPoints.push({
          name: transactionType || "End",
          coord: [dates[toIndex], toCandleValue[3]],
          symbol: "pin",
          symbolSize: 16,
          itemStyle: {
            color: darkMode ? "#8f8d7bff" : "#ada997ff",
            shadowBlur: 10,
            shadowColor: darkMode ? "#fff" : "#333",
          },
          label: {
            show: true,
            position: fromIndex === toIndex - 1 ? "bottom" : "top", // <-- adjust if adjacent
            formatter: getLabelText(transactionType),
            color: "#fff",
            backgroundColor: darkMode ? "rgba(255,255,255,0.2)" : "#000",
            borderRadius: 4,
            padding: [4, 6],
            fontSize: 12,
          },
        });
      }
    }
  }

  if (historicalDates && historicalDates.length > 0) {
    historicalDates.forEach((date) => {
      const normDate = normalizeDate(date);
      if (!normDate) return;
      const index = dates.findIndex((d) => d === normDate);
      if (index !== -1) {
        const candleValue = Array.isArray(ohlc[index])
          ? ohlc[index]
          : (ohlc[index] as any).value;
        historicalMarkPoints.push({
          name: "ABillion Pick",
          coord: [dates[index], candleValue[2]],
          symbol: "pin",
          symbolRotate: 180,
          symbolSize: 12,
          itemStyle: {
            color: darkMode ? "#FBBF24" : "#F59E0B",
            shadowBlur: 10,
            shadowColor: darkMode
              ? "rgba(251, 191, 36, 0.4)"
              : "rgba(245, 158, 11, 0.4)",
          },
        });
      }
    });
  }

  const maColors = {
    MA9: undefined, // default color
    MA20: !darkMode ? "#19C37D" : undefined,
    MA50: !darkMode ? "#FA7B17" : undefined,
    MA100: !darkMode ? "#FD1050" : undefined,
    MA200: !darkMode ? "#9f7aea" : undefined,
  };

  const average =
    filteredCandleData.reduce((acc, curr) => acc + curr.close, 0) /
    (filteredCandleData.length || 1);

  const legendData: any[] = [
    { name: "MA9", itemStyle: { color: maColors.MA9 } },
    { name: "MA20", itemStyle: { color: maColors.MA20 } },
    { name: "MA50", itemStyle: { color: maColors.MA50 } },
    { name: "MA100", itemStyle: { color: maColors.MA100 } },
    { name: "MA200", itemStyle: { color: maColors.MA200 } },
    { name: "Average", itemStyle: { color: "#94A3B8" } },
  ];

  if (historicalDates && historicalDates.length > 0) {
    legendData.push({
      name: "Picked-in past",
      itemStyle: { color: darkMode ? "#FBBF24" : "#F59E0B" },
      icon: "pin",
    });
  }

  legendData.push(
    {
      name: "Volume",
      itemStyle: { color: "#26a69a" },
    },
    {
      name: VOL_BASED_CANDLE_LEGEND_NAME,
      itemStyle: { color: "#089981", borderColor: "#FD1050" },
    },
  );

  const lastDataDateStr = dates.length > 0 ? dates[dates.length - 1] : null;
  const hasTodaysDate = lastDataDateStr === todayStr;

  const BUFFER_DAYS = hasTodaysDate || isConnected ? 5 : 0;

  const extendedDates = [...dates];

  if (extendedDates.length > 0 && BUFFER_DAYS > 0) {
    const lastDate = moment(extendedDates[extendedDates.length - 1]);

    for (let i = 1; i <= BUFFER_DAYS; i++) {
      extendedDates.push(lastDate.clone().add(i, "days").format("YYYY-MM-DD"));
    }
  }

  const start =
    extendedDates.length > 0 ? (startIndex / extendedDates.length) * 100 : 0;

  const end = 100;

  const extendedSelectedIndices = getLastTwelveMonthIndices(extendedDates);
  const extendedOhlc = [...ohlc, ...new Array(BUFFER_DAYS).fill("-")];
  const extendedVolumeWidthRatios = useMemo(
    () => [...volumeWidthRatios, ...new Array(BUFFER_DAYS).fill(0)],
    [volumeWidthRatios, BUFFER_DAYS],
  );
  const extendedRollingAvgVolumes = useMemo(
    () => [...rollingAvgVolumes, ...new Array(BUFFER_DAYS).fill("-")],
    [rollingAvgVolumes, BUFFER_DAYS],
  );
  const customCandleData = useMemo(
    () =>
      filteredCandleData.map((item, index) => [
        getCandleRenderOpen(item.open, item.close),
        item.close,
        item.low,
        item.high,
        volumeWidthRatios[index] ?? volumeWidthConfig.baseWidthRatio,
      ]),
    [filteredCandleData, volumeWidthRatios, volumeWidthConfig.baseWidthRatio],
  );

  const extendedCustomCandleData = useMemo(
    () => [...customCandleData, ...new Array(BUFFER_DAYS).fill("-")],
    [customCandleData, BUFFER_DAYS],
  );

  const extendedCustomVolumeData = useMemo(
    () => [
      ...rawVolumes.map((volume) => [volume]),
      ...new Array(BUFFER_DAYS).fill("-"),
    ],
    [rawVolumes, BUFFER_DAYS],
  );

  const getVolumeSeriesData = useCallback(
    (isCustom: boolean) => {
      return isCustom
        ? extendedCustomVolumeData
        : extendedCustomVolumeData.map((d, idx) => {
            if (d === "-" || d[0] === "-") return "-";
            const vol = Array.isArray(d) ? d[0] : d;
            let color = "#26a69a";
            if (idx < filteredCandleData.length) {
              const item = filteredCandleData[idx];
              if (item && item.close < item.open) color = "#ef5350";
            }
            return { value: vol, itemStyle: { color, opacity: 0.6 } };
          });
    },
    [extendedCustomVolumeData, filteredCandleData],
  );

  const getExtendedWidthRatio = useCallback(
    (dataIndex: number) =>
      extendedVolumeWidthRatios[dataIndex] ?? volumeWidthConfig.baseWidthRatio,
    [extendedVolumeWidthRatios, volumeWidthConfig.baseWidthRatio],
  );

  const isTodayAtIndex = useCallback(
    (dataIndex: number) => {
      const item = filteredCandleData[dataIndex];
      if (!item) return false;
      return (
        moment.utc(item.date).tz("Asia/Kolkata").format("YYYY-MM-DD") ===
          todayStr && isConnected
      );
    },
    [filteredCandleData, todayStr, isConnected],
  );

  const isBullishAtIndex = useCallback(
    (dataIndex: number) => {
      const item = filteredCandleData[dataIndex];
      if (!item) return true;
      return item.close >= item.open;
    },
    [filteredCandleData],
  );

  const isRenderableAtIndex = useCallback(
    (dataIndex: number) => dataIndex < filteredCandleData.length,
    [filteredCandleData.length],
  );

  const volumeCandleRenderItem = useMemo(
    () =>
      createVolumeCandleRenderItem({
        getWidthRatio: getExtendedWidthRatio,
        isTodayCandle: isTodayAtIndex,
        isBullishCandle: isBullishAtIndex,
        isRenderableAtIndex,
      }),
    [
      getExtendedWidthRatio,
      isTodayAtIndex,
      isBullishAtIndex,
      isRenderableAtIndex,
    ],
  );

  const volumeBarRenderItem = useMemo(
    () =>
      createVolumeBarRenderItem({
        getWidthRatio: getExtendedWidthRatio,
        getBarColor: (dataIndex) => {
          if (dataIndex >= filteredCandleData.length) return "#26a69a";
          const item = filteredCandleData[dataIndex];
          if (!item) return "#26a69a";
          return item.close > item.open ? "#26a69a" : "#ef5350";
        },
        isRenderableAtIndex,
      }),
    [getExtendedWidthRatio, filteredCandleData, isRenderableAtIndex],
  );

  const buildVolumeSeriesConfig = useCallback(
    (useVolWidth: boolean) => ({
      name: "Volume",
      type: useVolWidth ? ("custom" as const) : ("bar" as const),
      ...(useVolWidth
        ? { dimensions: ["volume"], encode: { x: -1, y: 0 } }
        : {}),
      renderItem: useVolWidth ? volumeBarRenderItem : undefined,
      data: getVolumeSeriesData(useVolWidth),
      xAxisIndex: 1,
      yAxisIndex: 1,
      z: 2,
      clip: true,
    }),
    [getVolumeSeriesData, volumeBarRenderItem],
  );

  const extendLine = (data: number[]) => [
    ...data,
    ...new Array(BUFFER_DAYS).fill("-"),
  ];

  const ma9 = extendLine(filteredCandleData.map((i) => i["9daysmaline"]));
  const ma20 = extendLine(filteredCandleData.map((i) => i["20daysmaline"]));
  const ma50 = extendLine(filteredCandleData.map((i) => i["50daysmaline"]));
  const ma100 = extendLine(filteredCandleData.map((i) => i["100daysmaline"]));
  const ma200 = extendLine(filteredCandleData.map((i) => i["200daysmaline"]));

  const defaultLegendSelected: Record<string, boolean> = {
    MA9: false,
    MA20: isOrderblock ? false : true,
    MA50: false,
    MA100: false,
    MA200: false,
    Average: false,
    Volume: true,
    "Picked-in past": false,
    [VOL_BASED_CANDLE_LEGEND_NAME]: false,
  };

  const savedLegend = getSavedLegendSelected();
  // Merge: start from defaults, override with whatever was saved
  const mergedLegendSelected = { ...defaultLegendSelected, ...savedLegend };
  const showVolBasedCandles =
    mergedLegendSelected[VOL_BASED_CANDLE_LEGEND_NAME] !== false;
  const candlestickBodyStyle = showVolBasedCandles
    ? TRANSPARENT_CANDLESTICK_ITEM_STYLE
    : STANDARD_CANDLESTICK_ITEM_STYLE;

  const visibleMaLines = [
    { data: ma9, include: mergedLegendSelected.MA9 !== false },
    { data: ma20, include: mergedLegendSelected.MA20 !== false },
    { data: ma50, include: mergedLegendSelected.MA50 !== false },
    { data: ma100, include: mergedLegendSelected.MA100 !== false },
    { data: ma200, include: mergedLegendSelected.MA200 !== false },
  ];
  const visiblePriceYExtras: number[] = [];
  if (mergedLegendSelected.Average !== false) visiblePriceYExtras.push(average);
  if (isLastToday && isConnected) visiblePriceYExtras.push(lastClose);

  const realDataLength = dates.length;
  const visiblePriceYRange = computeVisiblePriceYRange(
    extendedOhlc,
    start,
    end,
    visibleMaLines,
    visiblePriceYExtras,
    realDataLength,
  );
  const rawVisibleVolumeYMax = computeVisibleVolumeYMax(
    extendedCustomVolumeData,
    start,
    end,
    realDataLength,
  );
  const visibleVolumeYMax = rawVisibleVolumeYMax
    ? getNiceMax(rawVisibleVolumeYMax)
    : null;

  const isCardView = !isExpand;

  const option = {
    backgroundColor: "transparent",
    legend: {
      data: legendData,
      selected: mergedLegendSelected,
      inactiveColor: "#777",
      ...(isCardView
        ? {
            type: "scroll" as const,
            orient: "horizontal" as const,
            left: "center",
            top: 0,
            width:
              isOrderblock &&
              typeof window !== "undefined" &&
              window.innerWidth >= 1408 &&
              window.innerWidth <= 1600
                ? 390
                : "90%",
            itemGap: 10,
            pageIconSize: 9,
            pageButtonItemGap: 2,
            pageButtonGap: 3,
            pageButtonPosition: "end" as const,
            pageTextStyle: {
              fontSize: 9,
              color: darkMode ? "#9ca3af" : "#6b7280",
            },
            animationDurationUpdate: 0,
          }
        : {
            itemGap: 16,
          }),
      textStyle: {
        color: darkMode ? "#f8f9fa" : "#212529",
        fontSize: isCardView ? 10 : 12,
      },
      itemWidth: 14,
      itemHeight: 7,
      formatter: (name: string) => {
        if (name === VOL_BASED_CANDLE_LEGEND_NAME) {
          return `${name}  `;
        }
        return name;
      },
    },
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross",
      },
      confine: true,
      formatter: (
        params: {
          axisValue: string;
          seriesType: string;
          seriesName: string;
          color: string;
          data: BarData | CandlestickData | LineData;
          dataIndex: number;
        }[],
      ) => {
        const dateStr = params[0]?.axisValue;
        const todayStr = moment().tz("Asia/Kolkata").format("YYYY-MM-DD");
        const isToday = dateStr === todayStr && isConnected;

        let tooltipHtml = ``;
        const fmt = (val: number) =>
          Number(val).toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          });

        params.forEach((param) => {
          if (param.seriesType === "candlestick") {
            const idx = param.dataIndex as number;
            const candle: any = filteredCandleData[idx];

            // Safety check if candle doesn't exist for that index
            if (!candle) return;

            if (onHoverChange) {
              setTimeout(() => {
                onHoverChange({
                  ...candle,
                  isToday,
                });
              }, 0);
            }

            // OHLCV in tooltip for card view when header OHLCV is not shown
            // (no onHoverChange header, or layout hides the header row).
            if (
              !isExpand &&
              (!onHoverChange ||
                (typeof window !== "undefined" &&
                  isOhlcvHeaderHidden(moduleName, window.innerWidth)))
            ) {
              const priceLabel = isToday ? "LTP" : "Close";
              // Use your logic: Green if Close/LTP >= Open, else Red

              tooltipHtml += `
          <div style="margin-top: 4px;">
            Open: ${fmt(candle.open)}<br/>
            High: ${fmt(candle.high)}<br/>
            Low: ${fmt(candle.low)}<br/>
           ${priceLabel}: ${fmt(candle.close)}<br/>
            Volume: ${formatNumberWithSuffix(candle.volume ?? 0)}
          </div>`;
            }
          } else if (param.seriesType === "line") {
            const lineData = param.data;
            // Handle cases where line data might be an object, number, or null
            // NOTE: typeof null === "object" in JS, so we must guard first
            const val =
              lineData === null || lineData === undefined
                ? null
                : typeof lineData === "object" && !Array.isArray(lineData)
                  ? (lineData as BarData).value
                  : lineData;
            if (val === null || val === undefined || isNaN(Number(val))) {
              return "";
            }
            const isAvgVol = param.seriesName === "Avg Vol";
            const formattedLineValue = isAvgVol
              ? formatNumberWithSuffix(Number(val))
              : fmt(Number(val));

            tooltipHtml += `
          <div>
            <span style="color:${param.color}; margin-right:6px;">●</span>
            <b>${param.seriesName}: ${formattedLineValue}</b>
          </div>`;
          }
        });

        return tooltipHtml;
      },
    },

    axisPointer: {
      link: { xAxisIndex: "all" },
    },
    grid: [
      {
        left:
          isAnalyzeStock && !isExpand
            ? 30
            : isOrderblock && !isExpand
              ? 12
              : 30,
        top: "16%",
        height: isOrderblock && !isExpand ? "68%" : "70%",
        right: 50,
      },
      {
        left:
          isAnalyzeStock && !isExpand
            ? 30
            : isOrderblock && !isExpand
              ? 12
              : 30,
        bottom: isExpand ? 56 : isAnalyzeStock ? 42 : isOrderblock ? 41 : 47,
        height: "20%",
        right: 50,
      },
    ],
    xAxis: [
      {
        type: "category",
        gridIndex: 0,
        data: extendedDates,
        boundaryGap: true,
        axisLine: { show: true },
        axisTick: { show: true },
        splitLine: { show: false },
        axisLabel: {
          rotate: 35,
          formatter: (value: string) => {
            const today = moment().tz("Asia/Kolkata");
            const current = moment(value);
            const label = getMonthName(new Date(value));

            if (current.isAfter(today)) {
              return `${label}`;
            }

            return label;
          },

          interval: (index: number) => {
            return extendedSelectedIndices.includes(index);
          },
          hideOverlap: true,
        },
      },
      {
        type: "category",
        gridIndex: 1,
        data: extendedDates,
        boundaryGap: true,
        axisLine: { show: false },
        axisPointer: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
      },
    ],
    yAxis: [
      {
        // Same visible-window scaling for default and vol-width modes; buffer days
        // are excluded via realDataLength so trailing placeholders never affect axes.
        scale: !visiblePriceYRange,
        min: visiblePriceYRange?.min,
        max: visiblePriceYRange?.max,
        splitLine: { show: false },
        position: "right",
        gridIndex: 0,
        axisLabel: {
          formatter: (value: number) => formatNumber(value, true),
        },
      },
      {
        gridIndex: 1,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
        axisPointer: { label: { show: false } },
        min: 0,
        max: visibleVolumeYMax ?? undefined,
      },
    ],
    dataZoom: [
      {
        type: "inside",
        xAxisIndex: [0, 1],
        start,
        end,
        minSpan: minSpanPercentage,
        zoomLock: false,
        filterMode: "filter",
      },
      {
        type: "slider",
        xAxisIndex: [0, 1],
        start,
        end,
        show: false,
        zoomLock: false,
        minSpan: minSpanPercentage,
        filterMode: "filter",
      },
    ],

    series: [
      {
        type: "candlestick",
        name: displaySymbolName,
        data: extendedOhlc,
        xAxisIndex: 0,
        yAxisIndex: 0,
        clip: true,
        itemStyle: candlestickBodyStyle,
        emphasis: {
          itemStyle: candlestickBodyStyle,
        },
        markArea: {
          data: [...orderBlockMarkArea],
        },
        markPoint: {
          symbolKeepAspect: true,
          data: candlestickMarkPoints,
        },
        markLine: {
          animation: true,
          animationDuration: 400,
          animationEasing: "cubicOut",
          symbol: ["none", "none"],
          data:
            isLastToday && isConnected
              ? [
                  {
                    yAxis: lastClose,
                    label: {
                      show: true,
                      position: "end",
                      formatter: (params: any) =>
                        formatNumber(params.value, true),
                      backgroundColor: lastCandleColor,
                      color: "#fff",
                      padding: [2, 4],
                      borderRadius: 2,
                    },
                    lineStyle: {
                      type: "dashed",
                      color: lastCandleColor,
                      width: 1,
                    },
                  },
                ]
              : [],
        },
      },
      {
        type: "custom",
        name: VOL_BASED_CANDLE_LEGEND_NAME,
        dimensions: ["open", "close", "low", "high", "widthRatio"],
        encode: { x: -1, y: [0, 1, 2, 3] },
        renderItem: volumeCandleRenderItem,
        data: extendedCustomCandleData,
        xAxisIndex: 0,
        yAxisIndex: 0,
        z: 2,
        clip: true,
        silent: true,
        tooltip: { show: false },
      },
      {
        name: "MA9",
        type: "line",
        data: ma9,
        smooth: true,
        showSymbol: false,
        lineStyle: {
          width: 1,
        },
      },
      {
        name: "MA20",
        type: "line",
        data: ma20,
        smooth: true,
        showSymbol: false,
        lineStyle: {
          width: 1,
        },
      },
      {
        name: "MA50",
        type: "line",
        data: ma50,
        smooth: true,
        showSymbol: false,
        lineStyle: {
          width: 1,
          color: !darkMode ? "#19C37D" : undefined,
        },
      },
      {
        name: "MA100",
        type: "line",
        data: ma100,
        smooth: true,
        showSymbol: false,
        lineStyle: {
          width: 1,
          color: !darkMode ? "#FA7B17" : undefined,
        },
      },
      {
        name: "MA200",
        type: "line",
        data: ma200,
        smooth: true,
        showSymbol: false,
        lineStyle: {
          width: 1,
          color: !darkMode ? "#FD1050" : undefined,
        },
      },

      {
        name: "Average",
        type: "line",
        showSymbol: false,
        lineStyle: {
          opacity: 0,
        },
        data: extendedDates.map(() => average),
        markLine: {
          silent: true,
          symbol: ["none", "none"],
          lineStyle: {
            color: "#6c757d",
            width: 1.5,
            type: "dashed",
          },
          label: {
            show: true,
            position: "end",
            distance: [10, 8],
            verticalAlign: "bottom",
            align: "right",
            formatter: "52 week Avg: " + average.toFixed(2),
            backgroundColor: darkMode
              ? "rgba(20, 20, 20, 0.8)"
              : "rgba(255, 255, 255, 0.9)",
            padding: [5, 9],
            borderRadius: 4,
            fontSize: 12,
            color: darkMode ? "#f8f9fa" : "#212529",
          },
          data: [
            {
              yAxis: average,
            },
          ],
        },
      },

      ...(historicalDates && historicalDates.length > 0
        ? [
            {
              name: "Picked-in past",
              type: "scatter" as const,
              xAxisIndex: 0,
              yAxisIndex: 0,
              z: 10,
              data: [],
              symbol: "pin",
              itemStyle: {
                color: darkMode ? "#FBBF24" : "#F59E0B",
              },
              markPoint: {
                symbolKeepAspect: true,
                data: historicalMarkPoints,
              },
            },
          ]
        : []),

      ...(showVolBasedCandles
        ? [
            {
              name: "Avg Vol",
              type: "line" as const,
              data: extendedRollingAvgVolumes,
              xAxisIndex: 1,
              yAxisIndex: 1,
              smooth: true,
              showSymbol: false,
              lineStyle: {
                width: 1.5,
                color: darkMode ? "#ffffff" : "#1d1d1f", // bright blue so it stands out against volume bars
              },
              z: 3,
            },
          ]
        : []),

      buildVolumeSeriesConfig(showVolBasedCandles),
    ],
  };

  useEffect(() => {
    setVolWidthLegendOn(showVolBasedCandles);
  }, [showVolBasedCandles]);

  useEffect(() => {
    if (!volWidthLegendOn || isExpand) return;
    const chart = chartInstance.current;
    if (!chart) return;
    scrollLegendToVolWidth(chart);
    const raf = requestAnimationFrame(() => syncVolPeriodTriggerPosition());
    return () => cancelAnimationFrame(raf);
  }, [volWidthLegendOn, isExpand, syncVolPeriodTriggerPosition]);

  // Rebuild series on vol-width / period changes; avoid passing legend so MA lines
  // do not re-render when unrelated UI state (e.g. period dropdown) changes.
  useEffect(() => {
    if (!chartInstance.current) return;
    chartInstance.current.setOption(
      { series: option.series, yAxis: option.yAxis },
      { replaceMerge: ["series"] },
    );
    syncAxesToVisibleWindow(chartInstance.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showVolBasedCandles,
    volumePeriodDays,
    volumeBarRenderItem,
    volumeCandleRenderItem,
    extendedCustomCandleData,
    extendedVolumeWidthRatios,
    extendedRollingAvgVolumes,
    visiblePriceYRange?.min,
    visiblePriceYRange?.max,
    visibleVolumeYMax,
  ]);

  // The instance is created once per theme and kept alive for the lifetime of the
  // component; data changes are pushed through `setOption` below. Tearing the
  // instance down whenever a dependency changed made ECharts replay its intro
  // animation, so any prop that resolved after the candles (the historical pins,
  // the socket connecting) drew the whole chart a second time.
  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current, darkMode ? "dark" : "light");
    chartInstance.current = chart;

    const syncTrigger = () => syncVolPeriodTriggerPositionRef.current();
    chart.on("finished", syncTrigger);
    chart.on("legendscroll", () => {
      setVolumePeriodMenu((prev) => ({ ...prev, open: false }));
      syncTrigger();
    });
    syncTrigger();

    chart.on("datazoom", () => {
      syncAxesToVisibleWindow(chart);
    });

    chart.on("click", (params: any) => {
      // 1. MarkPoint click
      if (params.componentType === "markPoint") {
        const coordDate = params.data?.coord?.[0];
        const pointName = params.name || params.data?.name;
        if (coordDate && onHistoricalPointerClickRef.current) {
          onHistoricalPointerClickRef.current(String(coordDate));
          return;
        }
        if (
          pointName &&
          pointName !== "ABillion Pick" &&
          onHistoricalPointerClickRef.current
        ) {
          onHistoricalPointerClickRef.current(String(pointName));
          return;
        }
      }

      // 2. Click on the candle or bar for a historical pick date
      const clickedDate = params.name;
      if (
        clickedDate &&
        onHistoricalPointerClickRef.current &&
        historicalDatesRef.current
      ) {
        const normClicked = normalizeDate(String(clickedDate));
        const match = historicalDatesRef.current.find(
          (d) => normalizeDate(d) === normClicked,
        );
        if (match) {
          onHistoricalPointerClickRef.current(match);
        }
      }
    });

    chart.on("mouseover", (params: any) => {
      if (params.componentType === "markPoint") {
        chart.getZr().setCursorStyle("pointer");
        return;
      }
      const hoveredDate = params.name;
      if (hoveredDate && historicalDatesRef.current) {
        const normHovered = normalizeDate(String(hoveredDate));
        const isPick = historicalDatesRef.current.some(
          (d) => normalizeDate(d) === normHovered,
        );
        if (isPick) {
          chart.getZr().setCursorStyle("pointer");
        }
      }
    });

    chart.on("mouseout", () => {
      chart.getZr().setCursorStyle("default");
    });

    // Listen for legend toggles and persist to localStorage
    chart.on("legendselectchanged", (params: unknown) => {
      const legendEvent = params as {
        type: string;
        name: string;
        selected: Record<string, boolean>;
      };
      const selected = legendEvent.selected;

      saveLegendSelectedRef.current(selected);

      syncAxesToVisibleWindow(chart);

      if (legendEvent.name !== VOL_BASED_CANDLE_LEGEND_NAME) {
        return;
      }

      const volCandlesOn = selected[VOL_BASED_CANDLE_LEGEND_NAME] !== false;
      setVolWidthLegendOn(volCandlesOn);
      if (!volCandlesOn) {
        setVolumePeriodMenu((prev) => ({ ...prev, open: false }));
        setVolPeriodTriggerVisible(false);
      } else if (!isExpandRef.current) {
        scrollLegendToVolWidth(chart);
        requestAnimationFrame(() => syncVolPeriodTriggerPositionRef.current());
      } else {
        requestAnimationFrame(() => syncVolPeriodTriggerPositionRef.current());
      }
    });

    chart.on("globalout", () => {
      onHoverChangeRef.current?.(null);
    });

    // Sync legend from the sibling chart instance (card ↔ expand modal)
    const handleLegendSync = (e: Event) => {
      const selected = (e as CustomEvent<Record<string, boolean>>).detail;
      const volCandlesOn = selected[VOL_BASED_CANDLE_LEGEND_NAME] !== false;
      setVolWidthLegendOn(volCandlesOn);
      if (!volCandlesOn) {
        setVolumePeriodMenu((prev) => ({ ...prev, open: false }));
        setVolPeriodTriggerVisible(false);
      } else if (!isExpandRef.current) {
        scrollLegendToVolWidth(chart);
        requestAnimationFrame(() => syncVolPeriodTriggerPositionRef.current());
      }
    };
    window.addEventListener(LEGEND_SYNC_EVENT, handleLegendSync);

    const handleVolumePeriodSync = (e: Event) => {
      const days = (e as CustomEvent<number>).detail;
      if (isVolumeAvgPeriodOption(days)) {
        setVolumePeriodDays(days);
      }
    };
    window.addEventListener(VOL_PERIOD_SYNC_EVENT, handleVolumePeriodSync);

    const handleResize = () => {
      chart.resize();
      syncVolPeriodTriggerPositionRef.current();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener(LEGEND_SYNC_EVENT, handleLegendSync);
      window.removeEventListener(VOL_PERIOD_SYNC_EVENT, handleVolumePeriodSync);
      window.removeEventListener("resize", handleResize);
      chart.dispose();
      chartInstance.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [darkMode]);

  useEffect(() => {
    const chart = chartInstance.current;
    if (!chart) return;

    const currentOption = chart.getOption() as any;
    const currentDataZoom = currentOption?.dataZoom?.[0];
    const isSameSymbol = lastSymbolRef.current === symbolName;
    lastSymbolRef.current = symbolName;

    let finalOption = option;
    if (
      isSameSymbol &&
      currentDataZoom &&
      currentDataZoom.start != null &&
      currentDataZoom.end != null
    ) {
      finalOption = {
        ...option,
        dataZoom: (option.dataZoom as any[])?.map((dz) => ({
          ...dz,
          start: currentDataZoom.start,
          end: currentDataZoom.end,
        })),
      };
    }

    // replaceMerge series so volume-period width ratios & renderItem stay in sync
    chart.setOption(finalOption, { replaceMerge: ["series"] });
    syncAxesToVisibleWindow(chart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    candleData,
    darkMode,
    obDate,
    retestDate,
    fromDate,
    toDate,
    isConnected,
    isExpand,
    historicalDates,
  ]);

  useEffect(() => {
    if (!socket || !isConnected || !symbolName) return;

    const handleChartStockUpdate = (messages: any[]) => {
      const updated = messages.find((m: any) => m.symbol_name === symbolName);
      if (!updated || !chartInstance.current) return;

      const currentOption = chartInstance.current.getOption() as any;
      const datesData = currentOption.xAxis[0].data;
      const seriesData = [...currentOption.series[0].data];
      // Find the index of Volume series (case-insensitive)
      const volSeriesIndex = currentOption.series.findIndex(
        (s: { name: string }) => s.name?.toLowerCase() === "volume",
      );
      const volumeSeriesData =
        volSeriesIndex !== -1
          ? [...currentOption.series[volSeriesIndex].data]
          : [];
      const ltpDateStr = moment
        .utc(updated.ltp_date)
        .tz("Asia/Kolkata")
        .format("YYYY-MM-DD");
      const targetIndex = datesData.indexOf(ltpDateStr);

      if (targetIndex !== -1) {
        const o = Number(updated.open);
        const c = Number(updated.ltp);
        const l = Number(updated.low);
        const h = Number(updated.high);
        const v = Number(updated.volume);

        if (isNaN(o) || isNaN(c)) return;

        const isBullish = c >= o;
        const activeColor = isBullish ? "#089981" : "#FD1050";
        // Candle-render open only; raw `o` still feeds the tooltip/header below.
        const renderOpen = getCandleRenderOpen(o, c);

        // 1. Update the internal data reference so Tooltip stays accurate
        if (filteredCandleData[targetIndex]) {
          filteredCandleData[targetIndex].open = o;
          filteredCandleData[targetIndex].close = c;
          filteredCandleData[targetIndex].low = l;
          filteredCandleData[targetIndex].high = h;
          filteredCandleData[targetIndex].volume = v;
        }

        // 2. Update the ECharts series data (Candlestick)
        seriesData[targetIndex] = {
          value: [renderOpen, c, l, h, v], // Standard: [Open, Close, Low, High, Volume]
          itemStyle: {
            color: activeColor,
            color0: activeColor,
            borderColor: activeColor,
            borderColor0: activeColor,
            borderWidth: 2,
            shadowBlur: 10,
            shadowColor: activeColor,
          },
        };

        // 3. Update Volume series data if found
        if (volSeriesIndex !== -1) {
          const isCustomSeries =
            currentOption.series[volSeriesIndex].type === "custom";
          if (isCustomSeries) {
            volumeSeriesData[targetIndex] = [v];
          } else {
            volumeSeriesData[targetIndex] = {
              value: v,
              itemStyle: { color: activeColor, opacity: 0.6 },
            };
          }
        }

        const updatedWidthRatios = computeVolumeWidthRatios(
          filteredCandleData.map((item) => Number(item.volume) || 0),
          volumeWidthConfig,
        );
        const customCandleSeriesIndex = currentOption.series.findIndex(
          (s: { name: string }) => s.name === VOL_BASED_CANDLE_LEGEND_NAME,
        );
        const customCandleSeriesData =
          customCandleSeriesIndex !== -1
            ? [...currentOption.series[customCandleSeriesIndex].data]
            : [];

        if (
          customCandleSeriesIndex !== -1 &&
          customCandleSeriesData[targetIndex]
        ) {
          const existing = customCandleSeriesData[targetIndex];
          customCandleSeriesData[targetIndex] = Array.isArray(existing)
            ? [renderOpen, c, l, h, updatedWidthRatios[targetIndex]]
            : {
                ...(typeof existing === "object" ? existing : {}),
                value: [renderOpen, c, l, h, updatedWidthRatios[targetIndex]],
              };
        }

        // 4. Apply update without merging (to keep it fast)
        chartInstance.current.setOption({
          series: [
            {
              name: displaySymbolName,
              data: seriesData,
              markLine: {
                data: [
                  {
                    yAxis: c,
                    label: {
                      show: true,
                      position: "end",
                      formatter: (params: any) =>
                        formatNumber(params.value, true),
                      backgroundColor: activeColor,
                      color: "#fff",
                      padding: [2, 4],
                      borderRadius: 2,
                    },
                    lineStyle: {
                      type: "dashed",
                      color: activeColor,
                      width: 1,
                    },
                  },
                ],
              },
            },
            ...(customCandleSeriesIndex !== -1
              ? [
                  {
                    name: VOL_BASED_CANDLE_LEGEND_NAME,
                    type: "custom",
                    xAxisIndex: 0,
                    yAxisIndex: 0,
                    data: customCandleSeriesData,
                  },
                ]
              : []),
            ...(volSeriesIndex !== -1
              ? [
                  {
                    name: currentOption.series[volSeriesIndex].name || "Volume",
                    type: currentOption.series[volSeriesIndex].type,
                    xAxisIndex: 1,
                    yAxisIndex: 1,
                    data: volumeSeriesData,
                  },
                ]
              : []),
          ],
        });
        syncAxesToVisibleWindow(chartInstance.current);
      }
    };

    socket.on("stock_update", handleChartStockUpdate);
    return () => {
      socket.off("stock_update", handleChartStockUpdate);
    };
  }, [
    socket,
    isConnected,
    symbolName,
    displaySymbolName,
    filteredCandleData,
    volumeWidthConfig,
    volumePeriodDays,
  ]);

  return (
    <div
      ref={chartContainerRef}
      className={`relative w-full ${isAnalyzeStock || isExpand ? "h-full" : isOrderblock ? "h-full" : "h-85"}`}
      style={{ transform: "scale(0.98)" }}
    >
      {volWidthLegendOn && (
        <>
          <VolumePeriodTrigger
            value={volumePeriodDays}
            darkMode={darkMode}
            left={volPeriodTriggerLeft}
            visible={volPeriodTriggerVisible}
            onClick={openVolumePeriodMenu}
          />
          <VolumePeriodMenu
            open={volumePeriodMenu.open}
            anchor={{ x: volumePeriodMenu.x, y: volumePeriodMenu.y }}
            value={volumePeriodDays}
            onChange={(days) => {
              saveVolumePeriod(days);
              closeVolumePeriodMenu();
            }}
            onClose={closeVolumePeriodMenu}
            darkMode={darkMode}
            containerWidth={chartContainerWidth}
          />
        </>
      )}
      <div ref={chartRef} id="candle-chart" className="h-full w-full" />
    </div>
  );
};

export default React.memo(Candle);
